import axios from "axios";
import api, { type PaginatedResponse } from "./api";

export interface Device {
  id: string;
  virtual_site_id: string;
  asset_id: string | null;
  name: string;
  description: string | null;
  device_type: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  status: string;
  source_type: string;
  source_config: Record<string, unknown> | null;
  connection_status: string | null;
  last_seen_at: string | null;
  metadata_: Record<string, unknown> | null;
  sensor_count: number;
  created_at: string;
  updated_at: string;
}

export interface Sensor {
  id: string;
  device_id: string;
  asset_id: string | null;
  name: string;
  sensor_type: string | null;
  unit: string | null;
  status: string;
  source_channel: string | null;
  config: Record<string, unknown> | null;
  data: Record<string, unknown> | null;
  data_history: { readings?: Array<Record<string, unknown>> } | null;
  metadata_: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface DeviceWithSensors extends Device {
  sensors: Sensor[];
}

export interface CreateDevicePayload {
  virtual_site_id: string;
  asset_id?: string | null;
  name: string;
  description?: string | null;
  device_type?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  status?: string;
  source_type?: string;
  source_config?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

export interface UpdateDevicePayload {
  asset_id?: string | null;
  name?: string;
  description?: string | null;
  device_type?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  status?: string;
  source_type?: string;
  source_config?: Record<string, unknown> | null;
  connection_status?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface CreateSensorPayload {
  device_id: string;
  asset_id?: string | null;
  name: string;
  sensor_type?: string | null;
  unit?: string | null;
  status?: string;
  source_channel?: string | null;
  config?: Record<string, unknown> | null;
  data?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

export interface UpdateSensorPayload {
  asset_id?: string | null;
  name?: string;
  sensor_type?: string | null;
  unit?: string | null;
  status?: string;
  source_channel?: string | null;
  config?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
}

export interface SensorReadingPayload {
  data: Record<string, unknown>;
  append_history?: boolean;
  history_limit?: number;
}

export interface SourceField {
  key: string;
  label: string;
  type: "text" | "password" | "url" | "number" | "select";
  placeholder?: string;
  required: boolean;
  help?: string;
  options?: string[];
}

export interface SourceCatalogEntry {
  key: string;
  label: string;
  category: string;
  description: string;
  icon: string;
  fields: SourceField[];
}

export interface SimulatorChannel {
  key: string;
  label: string;
  unit: string;
  description: string;
}

export interface SensorTag {
  id: string;
  sensor_id: string;
  sensor_name: string | null;
  sensor_unit: string | null;
  sensor_type: string | null;
  device_id: string | null;
  device_name: string | null;
  point_cloud_id: string | null;
  digital_twin_id: string | null;
  geometry: Record<string, unknown>;
  label: string | null;
  color: string | null;
  opacity: number;
  metadata_: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface CreateSensorTagPayload {
  sensor_id: string;
  point_cloud_id?: string | null;
  digital_twin_id?: string | null;
  geometry: { x: number; y: number; z: number };
  label?: string | null;
  color?: string | null;
  opacity?: number;
}

export interface UpdateSensorTagPayload {
  geometry?: { x: number; y: number; z: number };
  label?: string | null;
  color?: string | null;
  opacity?: number;
}

export interface SensorDocumentVersion {
  id: string;
  document_id: string;
  version_number: number;
  file_name: string;
  file_size_bytes: number;
  content_type: string;
  document_type: string;
  status: string;
  is_current: boolean;
  change_note: string | null;
  uploaded_by: string | null;
  uploaded_by_email: string | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
}

export interface SensorDocument {
  id: string;
  sensor_id: string;
  virtual_site_id: string;
  name: string;
  description: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  versions: SensorDocumentVersion[];
}

export async function listDevices(params: {
  virtual_site_id: string;
  asset_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<Device>> {
  const res = await api.get("/instrumentation/devices", { params });
  return res.data;
}

export async function getDevice(id: string): Promise<DeviceWithSensors> {
  const res = await api.get(`/instrumentation/devices/${id}`);
  return res.data;
}

export async function createDevice(payload: CreateDevicePayload): Promise<Device> {
  const res = await api.post("/instrumentation/devices", payload);
  return res.data;
}

export async function updateDevice(
  id: string,
  payload: UpdateDevicePayload,
): Promise<Device> {
  const res = await api.put(`/instrumentation/devices/${id}`, payload);
  return res.data;
}

export async function deleteDevice(id: string): Promise<void> {
  await api.delete(`/instrumentation/devices/${id}`);
}

export async function listSensors(params: {
  virtual_site_id?: string;
  device_id?: string;
  asset_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<PaginatedResponse<Sensor>> {
  const res = await api.get("/instrumentation/sensors", { params });
  return res.data;
}

export async function getSensor(id: string): Promise<Sensor> {
  const res = await api.get(`/instrumentation/sensors/${id}`);
  return res.data;
}

export async function createSensor(payload: CreateSensorPayload): Promise<Sensor> {
  const res = await api.post("/instrumentation/sensors", payload);
  return res.data;
}

export async function updateSensor(
  id: string,
  payload: UpdateSensorPayload,
): Promise<Sensor> {
  const res = await api.put(`/instrumentation/sensors/${id}`, payload);
  return res.data;
}

export async function deleteSensor(id: string): Promise<void> {
  await api.delete(`/instrumentation/sensors/${id}`);
}

export async function postSensorReading(
  id: string,
  payload: SensorReadingPayload,
): Promise<Sensor> {
  const res = await api.post(`/instrumentation/sensors/${id}/readings`, payload);
  return res.data;
}

/** Ask the backend to push a single (or N) simulated reading onto the sensor.
 *  Only works when the parent device's `source_type` is `simulated`. */
export async function simulateTick(
  sensorId: string,
  body?: { count?: number; history_limit?: number },
): Promise<Sensor> {
  const res = await api.post(`/instrumentation/sensors/${sensorId}/simulate-tick`, body);
  return res.data;
}

export async function listSourceTypes(): Promise<SourceCatalogEntry[]> {
  const res = await api.get("/instrumentation/source-types");
  return res.data;
}

export async function listSimulatorChannels(): Promise<SimulatorChannel[]> {
  const res = await api.get("/instrumentation/simulator-channels");
  return res.data;
}

// ── Sensor 3D tags ────────────────────────────────────────────────────

export async function listSensorTags(params: {
  sensor_id?: string;
  point_cloud_id?: string;
  digital_twin_id?: string;
}): Promise<SensorTag[]> {
  const res = await api.get("/instrumentation/sensor-tags", { params });
  return res.data;
}

export async function createSensorTag(payload: CreateSensorTagPayload): Promise<SensorTag> {
  const res = await api.post("/instrumentation/sensor-tags", payload);
  return res.data;
}

export async function updateSensorTag(
  id: string,
  payload: UpdateSensorTagPayload,
): Promise<SensorTag> {
  const res = await api.put(`/instrumentation/sensor-tags/${id}`, payload);
  return res.data;
}

export async function deleteSensorTag(id: string): Promise<void> {
  await api.delete(`/instrumentation/sensor-tags/${id}`);
}

// ── Sensor documents (S3) ─────────────────────────────────────────────

export async function listSensorDocuments(sensorId: string): Promise<SensorDocument[]> {
  const res = await api.get("/instrumentation/sensor-documents", {
    params: { sensor_id: sensorId },
  });
  return res.data;
}

interface InitiateDocResponse {
  document_id: string;
  version_id: string;
  version_number: number;
  upload_url: string | null;
  s3_key: string;
  expires_in: number;
}

/** Browser→S3 direct upload for sensor documents (datasheets, certs, photos).
 *
 *  1. POST /sensor-documents/initiate-upload  → presigned PUT URL + version_id
 *  2. PUT the file straight to S3 (no API gateway in the middle)
 *  3. POST /sensor-documents/versions/{version_id}/complete-upload to flip status
 *
 *  In dev mode the backend returns upload_url=null; we skip the PUT and go
 *  straight to complete-upload.
 */
export async function uploadSensorDocument(
  payload: {
    sensor_id: string;
    document_id?: string | null;
    name?: string;
    description?: string;
    file: File;
    change_note?: string;
  },
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ document_id: string; version_id: string; version_number: number }> {
  const contentType = payload.file.type || "application/octet-stream";
  const initiate = await api.post<InitiateDocResponse>(
    "/instrumentation/sensor-documents/initiate-upload",
    {
      sensor_id: payload.sensor_id,
      document_id: payload.document_id ?? undefined,
      name: payload.name,
      description: payload.description,
      file_name: payload.file.name,
      file_size_bytes: payload.file.size,
      content_type: contentType,
      change_note: payload.change_note,
    },
  );
  const { document_id, version_id, upload_url, version_number } = initiate.data;

  try {
    if (upload_url) {
      await axios.put(upload_url, payload.file, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(e.loaded, e.total);
        },
        timeout: 0,
      });
    }
    await api.post(
      `/instrumentation/sensor-documents/versions/${version_id}/complete-upload`,
    );
  } catch (err) {
    // Best-effort cleanup of the half-created version so the doc list doesn't
    // accumulate ghost rows after a flaky upload.
    try {
      await api.delete(`/instrumentation/sensor-documents/versions/${version_id}`);
    } catch {
      // swallow — surface the original error
    }
    throw err;
  }

  return { document_id, version_id, version_number };
}

export async function getSensorDocumentViewUrl(
  versionId: string,
): Promise<{ view_url: string | null; expires_in: number; content_type: string; file_name: string; document_type: string }> {
  const res = await api.get(
    `/instrumentation/sensor-documents/versions/${versionId}/view-url`,
  );
  return res.data;
}

export async function deleteSensorDocumentVersion(versionId: string): Promise<void> {
  await api.delete(`/instrumentation/sensor-documents/versions/${versionId}`);
}

export async function deleteSensorDocument(documentId: string): Promise<void> {
  await api.delete(`/instrumentation/sensor-documents/${documentId}`);
}
